import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import "./AnalysisPage.css";
import { List } from "react-window";
import FixtureDetailPage from "./FixtureDetailPage";
import {
  classifySportForAnalysisTab,
  formatAnalysisOddsDisplay,
  getAnalysisStatusLabel,
  getAnalysisTranslations,
  getSteamMetaLabels,
  getSteamSignalLabel,
  translateLeagueDisplay,
  translateMarketDisplay,
  translateMarketDisplayWithPeriodSplit,
  translateSelectionDisplay,
  translateSteamFlag,
} from "./analysisTranslate";
import { auth } from "../../utils/firebase";
import {
  ANALYSIS_FETCH_ERROR_KINDS,
  analysisAuthenticatedFetch,
  isAnalysisAuthenticatedFetchError,
} from "./analysisAuthenticatedFetch";

// ─── Shared odds format sync ───────────────────────────────────
const ANALYSIS_ODDS_FORMAT_KEY = "analysis_options_odds_format";
const VALID_ODDS_FORMATS = ["hk", "decimal", "american"];

// ── Temp perf helpers — bật bằng window.__STEAM_PERF=true hoặc localStorage.__STEAM_PERF="true" ──
const isSteamPerfEnabled = () =>
  typeof window !== "undefined" &&
  (window.__STEAM_PERF === true || localStorage.getItem("__STEAM_PERF") === "true");

let __perfApplyCount = 0;
let __perfApplyWindowStart = 0;
let __perfCardRenderCount = 0;
let __perfCardLogTimer = null;

function readOddsFormatFromStorage() {
  try {
    const v = localStorage.getItem(ANALYSIS_ODDS_FORMAT_KEY);
    if (v && VALID_ODDS_FORMATS.includes(v.toLowerCase())) return v.toLowerCase();
  } catch { /* ignore */ }
  return "hk";
}

function useOddsFormat() {
  const [oddsFormat, setOddsFormat] = React.useState(readOddsFormatFromStorage);
  React.useEffect(() => {
    function sync() {
      const next = readOddsFormatFromStorage();
      setOddsFormat((prev) => (prev !== next ? next : prev));
    }
    // Cross-tab sync (storage event fires in other windows)
    window.addEventListener("storage", sync);
    // Same-window sync via custom event dispatched by settings page
    window.addEventListener("analysis_odds_format_changed", sync);
    // Fallback poll — chỉ đọc localStorage để sync odds format (hk/decimal/american).
    // Không liên quan đến data fetch, WebSocket, Steam, hay backend.
    // Nếu event không bắn đúng, fallback vẫn sync sau tối đa 2 giây.
    const iv = setInterval(sync, 2000);
    return () => {
      window.removeEventListener("storage", sync);
      window.removeEventListener("analysis_odds_format_changed", sync);
      clearInterval(iv);
    };
  }, []);
  return oddsFormat;
}
// ───────────────────────────────────────────────────────────────

let cachedAnalysisApiBase = null;

async function detectAnalysisApiBase() {
  if (cachedAnalysisApiBase !== null) return cachedAnalysisApiBase;
  const candidates = ["", "http://localhost:3001"];
  for (const base of candidates) {
    try {
      const ctrl = new AbortController();
      const t = setTimeout(() => ctrl.abort(), 4000);
      const res = await fetch(`${base}/api/_selftest`, { signal: ctrl.signal });
      clearTimeout(t);
      if (!res.ok) continue;
      const j = await res.json().catch(() => null);
      if (j && j.ok === true) {
        cachedAnalysisApiBase = base;
        return base;
      }
    } catch {
      /* next */
    }
  }
  cachedAnalysisApiBase = "";
  return "";
}

function isBlockingSteamAccessError(error) {
  if (!isAnalysisAuthenticatedFetchError(error)) return false;

  return (
    error.kind === ANALYSIS_FETCH_ERROR_KINDS.unauthorized ||
    error.kind === ANALYSIS_FETCH_ERROR_KINDS.account_disabled ||
    error.kind === ANALYSIS_FETCH_ERROR_KINDS.entitlement ||
    error.kind === ANALYSIS_FETCH_ERROR_KINDS.feature_forbidden
  );
}

function httpBaseToAnalysisWsUrl(base) {
  try {
    const b = String(base ?? "").trim();
    if (!b) {
      if (typeof window === "undefined") return "ws://localhost:3001/api/analysis/ws";
      const p = window.location.protocol === "https:" ? "wss:" : "ws:";
      return `${p}//${window.location.host}/api/analysis/ws`;
    }
    const u = new URL(b, typeof window !== "undefined" ? window.location.href : "http://localhost");
    const wsP = u.protocol === "https:" ? "wss:" : "ws:";
    return `${wsP}//${u.host}/api/analysis/ws`;
  } catch {
    return null;
  }
}

function pickSteamMarket(raw) {
  const m =
    raw.market ??
    raw.marketType ??
    raw.market_type ??
    raw.marketName ??
    raw.fixture?.market ??
    raw.fixture?.marketName ??
    raw.bet?.market ??
    raw.bet?.marketType ??
    "";
  return String(m || "").trim();
}

function pickSteamSelection(raw) {
  const s =
    raw.selection ??
    raw.pick ??
    raw.selectionName ??
    raw.outcome ??
    raw.outcomeName ??
    raw.bet?.selection ??
    raw.bet?.outcome ??
    "";
  return String(s || "").trim();
}

function normalizeSteamRow(raw) {
  if (!raw || typeof raw !== "object") return null;
  const market = pickSteamMarket(raw);
  const selection = pickSteamSelection(raw);

  if (raw.match != null && String(raw.match).trim() && raw.pct != null && raw.odds != null) {
    const odds = raw.newOdd ?? raw.odds ?? raw.newDecimalOdd;
    const trueOdds = raw.oldOdd ?? raw.trueOdds ?? raw.oldDecimalOdd;
    const pct = Math.abs(Number(raw.absPct ?? raw.pct ?? raw.deltaPct ?? 0));
    const m = market || raw.market || "";
    const s = selection || raw.selection || "";
    return {
      ...raw,
      id: raw.id ?? `${raw.fixture?.id || ""}-${m}-${s}`,
      market: m,
      selection: s,
      odds,
      trueOdds,
      pct: Number.isFinite(pct) ? pct : 0,
    };
  }
  const home = raw.homeName ?? raw.homeTeam ?? raw.fixture?.teams?.home?.name ?? "";
  const away = raw.awayName ?? raw.awayTeam ?? raw.fixture?.teams?.away?.name ?? "";
  const match =
    String(raw.match || "").trim() ||
    (home && away ? `${home} vs ${away}` : String(home || away || "").trim() || "—");
  const pct = Math.abs(
    Number(raw.absPct ?? raw.pct ?? raw.deltaPct ?? 0)
  );
  const odds = raw.newOdd ?? raw.odds ?? raw.newDecimalOdd;
  const trueOdds = raw.oldOdd ?? raw.trueOdds ?? raw.oldDecimalOdd;
  const league =
    raw.league ??
    raw.leagueName ??
    raw.fixture?.league?.name ??
    raw.fixture?.league ??
    "";
  const kickoffDate =
    raw.kickoffDate ??
    raw.kickoff ??
    raw.fixture?.date ??
    raw.fixture?.startTime ??
    raw.startsAt ??
    "";
  const mid = market || "market";
  const sid = selection || "sel";
  return {
    ...raw,
    id: raw.id ?? `${raw.fixture?.id || ""}-${mid}-${sid}`,
    league,
    match,
    market: market || "",
    selection: selection || "",
    bookmaker: raw.bookmaker || raw.bookmakerName || "",
    odds,
    trueOdds,
    pct: Number.isFinite(pct) ? pct : 0,
    kickoffDate: kickoffDate || raw.kickoffDate,
    kickoff: kickoffDate || raw.kickoff,
    isLive: raw.isLive === true,
  };
}

function steamLineAlreadyInSelection(sel, line) {
  const rawSel = String(sel ?? "").trim();
  const rawLine = String(line ?? "").trim();
  if (!rawSel || !rawLine) return false;
  if (rawLine === "—" || rawLine === "-") return false;
  const lineCore = rawLine.replace(/\s+/g, "");
  if (!lineCore) return false;
  const escaped = lineCore.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const re = new RegExp(`(^|[^0-9.,])${escaped}($|[^0-9])`, "i");
  return re.test(rawSel);
}

function resolveSteamPeriodScopeLabelVi(periodRaw) {
  const token = String(periodRaw ?? "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");

  if (!token) return "";
  if (token === "ft" || token === "full_time" || token === "fulltime") return "Toàn Trận";
  if (token === "1h" || token === "1st_half" || token === "first_half" || token === "h1" || token === "1") {
    return "Hiệp 1";
  }
  if (token === "2h" || token === "2nd_half" || token === "second_half" || token === "h2" || token === "2") {
    return "Hiệp 2";
  }
  if (token === "1q" || token === "1st_quarter") return "Q1";
  if (token === "2q" || token === "2nd_quarter") return "Q2";
  return "";
}

const PERIOD_PREFIXES = {
  "1st_half_": "Hiệp 1",
  "first_half_": "Hiệp 1",
  "2nd_half_": "Hiệp 2",
  "second_half_": "Hiệp 2",
  "1st_quarter_": "Q1",
  "first_quarter_": "Q1",
  "2nd_quarter_": "Q2",
  "second_quarter_": "Q2",
  "3rd_quarter_": "Q3",
  "4th_quarter_": "Q4",
  "1st_period_": "Hiệp 1",
  "2nd_period_": "Hiệp 2",
  "3rd_period_": "Hiệp 3",
};

// Canonical period key dùng cho groupKey — tách biệt H1/H2/FT
const PERIOD_LABEL_TO_CANON = {
  "Hiệp 1": "first_half",
  "Hiệp 2": "second_half",
  "Hiệp 3": "period_3",
  "Q1": "q1", "Q2": "q2", "Q3": "q3", "Q4": "q4",
};

// Detect canonical period từ market type string (ưu tiên trước marketScope)
function detectPeriodFromMarketStr(mStr) {
  const split = extractPeriodFromMarketType(String(mStr || ""));
  if (!split.periodLabel) return "";
  return PERIOD_LABEL_TO_CANON[split.periodLabel]
    || split.periodLabel.toLowerCase().replace(/\s+/g, "_");
}

// Detect canonical period từ 1 raw period field value (period, periodKey, gamePeriod, …)
function detectCanonicalPeriod(raw) {
  const s = String(raw || "").trim().toLowerCase().replace(/[\s-]+/g, "_");
  if (!s) return "";
  if (s === "first_half" || s === "1st_half" || s === "h1" || s === "1h" || s === "ht") return "first_half";
  if (s === "second_half" || s === "2nd_half" || s === "h2" || s === "2h") return "second_half";
  if (s === "full_time" || s === "fulltime" || s === "ft" || s === "regulation") return "full_time";
  if (s === "q1" || s === "1st_quarter" || s === "first_quarter") return "q1";
  if (s === "q2" || s === "2nd_quarter" || s === "second_quarter") return "q2";
  if (s === "q3" || s === "3rd_quarter" || s === "third_quarter") return "q3";
  if (s === "q4" || s === "4th_quarter" || s === "fourth_quarter") return "q4";
  // fallback via PERIOD_LABEL_TO_CANON (handles Vietnamese labels like "Hiệp 1")
  return PERIOD_LABEL_TO_CANON[String(raw || "").trim()] || "";
}

// Detect canonical period từ marketScope string — xử lý mọi H1 variant
function detectCanonicalPeriodFromScope(scopeStr) {
  const s = String(scopeStr || "").toLowerCase();
  if (!s) return "";
  // H1: "1st_half", "first_half", "1st half", "first half", "h1", "1h"
  if (/1st[_\s]half|first[_\s]half|\bh1\b|\b1h\b/.test(s)) return "first_half";
  // H2
  if (/2nd[_\s]half|second[_\s]half|\bh2\b|\b2h\b/.test(s)) return "second_half";
  // Quarters
  if (/\bq1\b|1st[_\s]quarter|first[_\s]quarter/.test(s)) return "q1";
  if (/\bq2\b|2nd[_\s]quarter|second[_\s]quarter/.test(s)) return "q2";
  if (/\bq3\b|3rd[_\s]quarter|third[_\s]quarter/.test(s)) return "q3";
  if (/\bq4\b|4th[_\s]quarter|fourth[_\s]quarter/.test(s)) return "q4";
  // Explicit FT only — do NOT fallback to FT here; caller handles final fallback
  if (/\bft\b|full[_\s]time|fulltime/.test(s)) return "full_time";
  return "";
}

function extractPeriodFromMarketType(marketType) {
  const s = String(marketType || "").toLowerCase();
  for (const [prefix, label] of Object.entries(PERIOD_PREFIXES)) {
    if (s.startsWith(prefix)) {
      return { baseMarket: s.slice(prefix.length), periodLabel: label };
    }
  }
  return { baseMarket: s, periodLabel: null };
}

function steamMarketLabelAlreadyIncludesPeriod(display, periodLabel) {
  const market = String(display || "").trim();
  const m = market.toLowerCase();
  const p = String(periodLabel || "").toLowerCase();
  if (!m || !p) return false;
  if (m.includes(p)) return true;

  if (p === "hiệp 1") {
    return /\b(hiệp\s*1|1st\s*half|first\s*half|h1|ht)\b/i.test(m);
  }
  if (p === "hiệp 2") {
    return /\b(hiệp\s*2|2nd\s*half|second\s*half|h2|2h)\b/i.test(m);
  }
  if (p === "toàn trận") {
    return /\b(toàn\s*trận|cả\s*trận|full\s*time|fulltime|ft)\b/i.test(m);
  }
  if (p === "q1") {
    return /\b(q1|1st\s*quarter|first\s*quarter)\b/i.test(m);
  }
  if (p === "q2") {
    return /\b(q2|2nd\s*quarter|second\s*quarter)\b/i.test(m);
  }
  return false;
}

function getSteamMarketRowLabel(bet, lang) {
  const rawMarket = String(bet?.market ?? "").trim();
  if (!rawMarket) return translateMarketDisplay(rawMarket, lang);

  // Sharp API nhúng period trong market_type ("1st_half_point_spread")
  // bet.market có thể đã bị server normalize thành "point_spread" → mất period
  // Ưu tiên market_type để extract period, fallback về market nếu không có
  const rawForPeriod =
    String(bet?.market_type ?? bet?.marketType ?? "").trim() || rawMarket;
  const periodSplit = extractPeriodFromMarketType(rawForPeriod);
  const baseMarketRaw = String(periodSplit.baseMarket || "").trim() || rawMarket;
  const s = baseMarketRaw.replace(/_/g, " ").replace(/\s+/g, " ").trim();
  const lineRaw = bet?.line ?? bet?.point ?? bet?.handicap ?? bet?.total;
  const lineStr =
    lineRaw != null && String(lineRaw).trim() && String(lineRaw).trim() !== "—" && String(lineRaw).trim() !== "-"
      ? String(lineRaw).trim()
      : "";
  const selRaw = String(bet?.selection ?? "").trim();
  const periodRaw = bet?.period ?? bet?.scope ?? bet?.periodScope ?? bet?.half;

  const appendPeriodAndLine = (display) => {
    const periodLabel =
      periodSplit.periodLabel ||
      bet?._periodLabel ||
      resolveSteamPeriodScopeLabelVi(periodRaw);

    // Nếu không tìm thấy period → Sharp API ngầm hiểu là Toàn Trận
    // (tab Mùi chỉ hiện bóng đá, nên fallback này luôn đúng)
    const finalPeriodLabel = periodLabel || "Toàn Trận";

    let nextDisplay = display;
    if (finalPeriodLabel && !steamMarketLabelAlreadyIncludesPeriod(nextDisplay, finalPeriodLabel)) {
      nextDisplay = `${nextDisplay} · ${finalPeriodLabel}`;
    }
    // Line không append vào market label — đã hiển thị riêng ở hàng selection bên dưới
    return nextDisplay;
  };

  const isVi = String(lang || "").toLowerCase().startsWith("vi");
  if (!isVi) {
    return appendPeriodAndLine(translateMarketDisplayWithPeriodSplit(s, lang));
  }

  const totalsWithSide = s.match(
    /^(?:total\s+)?(over|under)\s+([0-9]+(?:\.[0-9]+)?(?:\s*\/\s*[0-9]+(?:\.[0-9]+)?)?)/i
  );
  if (totalsWithSide) {
    const side = String(totalsWithSide[1] || "").toLowerCase();
    const line = String(totalsWithSide[2] || "").trim();
    if (side === "over") return appendPeriodAndLine(`Tài ${line}`);
    if (side === "under") return appendPeriodAndLine(`Xỉu ${line}`);
  }

  const handicapWithLine = s.match(/^(asian\s+handicap|handicap|spread|run\s+line|point\s+spread)\s+(.+)$/i);
  if (handicapWithLine) {
    const kind = String(handicapWithLine[1] || "").toLowerCase().replace(/\s+/g, " ").trim();
    const line = String(handicapWithLine[2] || "").trim();
    if (!line) return appendPeriodAndLine(translateMarketDisplayWithPeriodSplit(s, lang));
    if (kind === "asian handicap") return appendPeriodAndLine(`Kèo chấp châu Á ${line}`);
    return appendPeriodAndLine(`Kèo chấp ${line}`);
  }

  return appendPeriodAndLine(translateMarketDisplayWithPeriodSplit(s, lang));
}

function shouldHideSteamSelectionRow(displayMarket, displaySelection) {
  const m = String(displayMarket || "").trim();
  const s = String(displaySelection || "").trim();
  if (!m || !s) return false;

  const ml = m.toLowerCase();
  const sl = s.toLowerCase();

  const semanticTokens = new Set(["tài", "xỉu", "over", "under"]);
  if (!semanticTokens.has(sl)) return false;

  const startsWithToken = ml.startsWith(`${sl} `);
  if (!startsWithToken) return false;

  return /^(tài|xỉu|over|under)\s+[0-9]+(?:\.[0-9]+)?(?:\/[0-9]+(?:\.[0-9]+)?)?/i.test(m);
}

function getSteamTierMeta(bet, lang) {
  const tier = String(bet?.signalTier || bet?.steamTier || bet?.severity || "").toLowerCase();
  const label = getSteamSignalLabel(lang, tier);
  if (tier === "yellow") {
    return {
      key: "yellow",
      label,
      badgeBg: "#ca8a04",
      badgeColor: "#fffbeb",
      border: "1px solid rgba(234, 179, 8, 0.65)",
      shellBg: "linear-gradient(180deg, rgba(254, 243, 199, 0.12) 0%, rgba(15, 23, 42, 0.35) 100%)",
    };
  }
  if (tier === "orange") {
    return {
      key: "orange",
      label,
      badgeBg: "#ea580c",
      badgeColor: "#fff7ed",
      border: "1px solid rgba(249, 115, 22, 0.65)",
      shellBg: "linear-gradient(180deg, rgba(255, 237, 213, 0.12) 0%, rgba(15, 23, 42, 0.35) 100%)",
    };
  }
  if (tier === "green") {
    return {
      key: "green",
      label,
      badgeBg: "#16a34a",
      badgeColor: "#ecfdf5",
      border: "1px solid rgba(34, 197, 94, 0.65)",
      shellBg: "linear-gradient(180deg, rgba(220, 252, 231, 0.12) 0%, rgba(15, 23, 42, 0.35) 100%)",
    };
  }
  return null;
}

const PCT_OPTIONS = [1, 3, 5, 8, 10, 15];
const FAVORITES_STORAGE_KEY = "analysis_favorite_ids";
const STEAM_LIST_ROW_HEIGHT = 184;
const STEAM_LIST_OVERSCAN = 6;
const STEAM_LIST_ROW_GAP = 8;

/* ------------------------------------------------ */

function renderMatchWithColoredAt(match) {
  const raw = String(match ?? "");
  if (!raw) return raw;
  const parts = raw.split(" @ ");
  if (parts.length !== 2) return raw;
  return (
    <>
      <span>{parts[0]}</span>
      <span className="analysis-at-sep"> @ </span>
      <span>{parts[1]}</span>
    </>
  );
}

function formatLineToken(v) {
  if (v == null) return null;
  const s = String(v).trim();
  if (!s || s === "—" || s === "-") return null;
  const num = Number(s);
  if (Number.isFinite(num)) {
    const r = Math.round(num * 100) / 100;
    if (r > 0) return `+${r}`;
    return String(r);
  }
  return s;
}

function formatOddsToken(v, oddsFormat = "hk") {
  const fmt = formatAnalysisOddsDisplay(v, oddsFormat);
  if (fmt != null) return String(fmt);
  if (v == null) return null;
  const n = Number(v);
  if (Number.isFinite(n)) return String(Math.round(n * 100) / 100);
  return String(v);
}

function getBetLineMoveAbs(bet) {
  const direct = Number(
    bet?.lineMoveAbs ??
    bet?.lineMove?.abs ??
    bet?.lineMovementAbs ??
    bet?.lineShiftAbs ??
    bet?.steamDetail?.displayDrop?.lineMoveAbs
  );

  if (Number.isFinite(direct)) return Math.abs(direct);

  const oldLine = Number(bet?.oldLine ?? bet?.openingLine ?? bet?.firstLine);
  const newLine = Number(bet?.newLine ?? bet?.line ?? bet?.point ?? bet?.handicap ?? bet?.total);

  if (Number.isFinite(oldLine) && Number.isFinite(newLine)) {
    return Math.abs(newLine - oldLine);
  }

  return 0;
}

function formatMovePctToken(movePct, direction) {
  if (movePct == null) return null;
  const n = Number(movePct);
  if (!Number.isFinite(n)) return null;
  const abs = Math.abs(n);
  const dir =
    direction === "down"
      ? "-"
      : direction === "up"
        ? "+"
        : n < 0
          ? "-"
          : "+";
  return `${dir}${abs.toFixed(2)}%`;
}

function formatAgeToken(sec) {
  if (sec == null) return null;
  const n = Number(sec);
  if (!Number.isFinite(n) || n < 0) return null;
  if (n < 90) return `${Math.round(n)}s`;
  const m = Math.floor(n / 60);
  const s = Math.round(n - m * 60);
  return s > 0 ? `${m}m${s}s` : `${m}m`;
}

function formatSteamPct(value, options = {}) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "";
  const abs = Math.abs(n);
  const sign = options.forceSign === false ? (n < 0 ? "-" : "") : (n >= 0 ? "+" : "-");
  return `${sign}${abs.toFixed(2)}%`;
}

function formatSteamPlainPct(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "";
  return `${n.toFixed(2).replace(/\.?0+$/, "")}%`;
}

function isDuplicateSteamFlag(flag, { isPinnacleMove }) {
  const s = String(flag || "").toLowerCase().replace(/[^a-z0-9]+/g, "");
  if (isPinnacleMove && (s.includes("pinnaclemove") || s === "pinnacle")) return true;
  return false;
}

const LEAGUE_ABBREV_MAP = {
  "english premier league": "EPL",
  "premier league": "EPL",
  "epl": "EPL",
  "la liga": "La Liga",
  "bundesliga": "BL",
  "1. bundesliga": "BL",
  "serie a": "SA",
  "ligue 1": "L1",
  "champions league": "UCL",
  "uefa champions league": "UCL",
  "europa league": "UEL",
  "uefa europa league": "UEL",
  "fa cup": "FA Cup",
  "carabao cup": "EFL Cup",
  "mls": "MLS",
  "serie b": "SB",
};

function abbreviateLeague(name) {
  if (!name) return name;
  const key = name.trim().toLowerCase();
  return LEAGUE_ABBREV_MAP[key] ?? name;
}

function splitMatchTeams(matchStr) {
  if (!matchStr) return null;
  let parts = matchStr.split(" vs ");
  if (parts.length === 2) return { home: parts[0].trim(), away: parts[1].trim() };
  parts = matchStr.split(" @ ");
  if (parts.length === 2) return { home: parts[0].trim(), away: parts[1].trim() };
  parts = matchStr.split(" v ");
  if (parts.length === 2) return { home: parts[0].trim(), away: parts[1].trim() };
  return null;
}

function abbrevTeam(name) {
  if (!name) return "";
  return name.trim();
}

function isLiveMatch(b) {
  if (!b || typeof b !== "object") return false;

  const kickoffRaw =
    b.kickoff ??
    b.kickoffDate ??
    b.startsAt ??
    b.fixture?.date ??
    b.fixture?.startTime ??
    null;

  const kickoffMs = kickoffRaw ? new Date(kickoffRaw).getTime() : null;
  const nowMs = Date.now();

  if (Number.isFinite(kickoffMs) && kickoffMs > nowMs + 60_000) {
    return false;
  }

  const minuteCandidates = [
    b.matchMinute,
    b.minute,
    b.gameMinute,
    b.elapsed,
    b.elapsed_min,
    b.gameClock,
  ];

  const hasLiveMinute = minuteCandidates.some((v) => {
    if (v == null || v === "") return false;
    const n = typeof v === "number" ? v : parseInt(String(v), 10);
    return Number.isFinite(n) && n >= 0 && n <= 130;
  });

  if (hasLiveMinute) return true;

  const statusText = [
    b.status,
    b.matchStatus,
    b.state,
    b.phase,
    b.gameState,
    b.period,
    b.livePeriod,
  ].map((v) => String(v ?? "").toLowerCase()).join(" ");

  if (
    /\bin[_\s-]?play\b|\blive\b|\bplaying\b|\b1h\b|\b2h\b|\bfirst[_\s-]?half\b|\bsecond[_\s-]?half\b|\bhalftime\b|\bht\b/.test(statusText)
  ) {
    return true;
  }

  const scopeText = String(
    b.thresholdScope ??
    b.dropScope ??
    b.scope ??
    ""
  ).toLowerCase();

  if (scopeText === "live") return true;

  if (b.isLive === true) return true;

  return false;
}

function hkToDisplayOdds(hkOdds, fmt) {
  const h = Number(hkOdds);
  if (!Number.isFinite(h) || h <= 0) return null;
  // bet.odds is stored as HK (0.91), convert to decimal (1.91)
  const decimal = h + 1;
  return formatAnalysisOddsDisplay(decimal, fmt);
}

const CARD_HK_MIN = 0.65;
const CARD_HK_MAX = 1.55;

/**
 * Extract display-ready data from bet.steamPressure.
 * Computes pressureShare only from drops in HK range 0.65–1.55,
 * matching FixtureDetailPage logic.
 * Returns null if no valid pressure data.
 */
function getSteamPressureDisplay(bet) {
  const sp = bet?.steamPressure;
  if (!sp || typeof sp !== "object") return null;
  if (!sp.sides || typeof sp.sides !== "object") return null;

  const sideKeys = Object.keys(sp.sides);
  if (sideKeys.length === 0) return null;

  // Tính cumulative drop cho từng side — chỉ drops HK 0.65–1.55
  const sideCums = sideKeys.map((key) => {
    const sideData = sp.sides[key];
    const drops = Array.isArray(sideData?.drops) ? sideData.drops : [];
    const visibleDrops = drops.filter((dr) => {
      const oldDec = Number(dr.oldOdds ?? 0);
      if (!Number.isFinite(oldDec) || oldDec <= 1) return true;
      const oldHk = oldDec - 1;
      return oldHk >= CARD_HK_MIN && oldHk <= CARD_HK_MAX;
    });
    const total = visibleDrops.reduce((s, d) => s + (d.singleDropPct ?? d.dropPct ?? 0), 0);
    const prematch = visibleDrops
      .filter((d) => d.phase === null)
      .reduce((s, d) => s + (d.singleDropPct ?? d.dropPct ?? 0), 0);
    const live = visibleDrops
      .filter((d) => d.phase !== null)
      .reduce((s, d) => s + (d.singleDropPct ?? d.dropPct ?? 0), 0);
    return {
      key,
      label: sideData.label || key,
      total,
      prematch,
      live,
      dropCount: sideData.dropCount ?? 0,
    };
  });

  const totalCum = sideCums.reduce((s, c) => s + c.total, 0);
  const totalPre = sideCums.reduce((s, c) => s + c.prematch, 0);
  const totalLive = sideCums.reduce((s, c) => s + c.live, 0);

  const pct = (num, den) =>
    den > 0 ? Math.min(100, Math.round((num / den) * 1000) / 10) : 0;

  const sidesArray = sideCums
    .map((c) => ({
      key: c.key,
      label: c.label,
      pressureShare: pct(c.total, totalCum),
      prematchShare: pct(c.prematch, totalPre),
      liveShare: pct(c.live, totalLive),
      cumulativeDrop: Math.round(c.total * 100) / 100,
      dropCount: c.dropCount,
    }))
    .sort((a, b) => b.pressureShare - a.pressureShare);

  if (sidesArray.every((s) => s.pressureShare === 0)) return null;

  return {
    sidesArray,
    leadingSide: sidesArray[0]?.label ?? null,
    leadingPressureShare: sidesArray[0]?.pressureShare ?? 0,
    totalCumulativeDrop: Math.round(totalCum * 100) / 100,
    peakPhase: sp.peakPhase ?? null,
    isLive: bet.thresholdScope === "live" || bet.isLive === true,
  };
}

function getLatestDropInfo(bet) {
  const sp = bet?.steamPressure;
  if (!sp?.sides) return null;
  const allDrops = Object.values(sp.sides)
    .flatMap((s) => Array.isArray(s.drops) ? s.drops : []);
  if (allDrops.length === 0) return null;
  const sorted = [...allDrops].sort((a, b) => {
    const tA = a.detectedAt ? new Date(a.detectedAt).getTime() : (a.recordedAt || 0);
    const tB = b.detectedAt ? new Date(b.detectedAt).getTime() : (b.recordedAt || 0);
    return tA - tB;
  });
  const latest = sorted[sorted.length - 1];
  if (!latest) return null;
  const ts = latest.detectedAt || latest.recordedAt || null;
  const timeStr = ts
    ? new Date(ts).toLocaleString("vi-VN", {
        day: "2-digit", month: "2-digit",
        hour: "2-digit", minute: "2-digit", second: "2-digit",
      })
    : null;
  return {
    minute: latest.minute ?? null,
    timeStr,
  };
}

function getCardGroupKeyStable(b) {
  const norm = (v) => String(v ?? "").trim().toLowerCase().replace(/\s+/g, "_");
  const eventKey = norm(b?.event_key || b?.eventId || b?.event_id || b?.fixtureId || b?.id || "");
  const mFamily  = norm(b?.marketFamily || b?.market_family || b?.marketGroup || b?.market || b?.market_type || b?.marketType || "");

  // Step 1: Ưu tiên các dedicated period fields — rõ ràng nhất, ít bị normalize sai nhất
  let period = "";
  const dedicatedFields = [
    b?.period, b?.periodKey, b?.gamePeriod, b?.marketPeriod,
    b?.periodScope, b?.scope, b?._periodLabel,
  ];
  for (const raw of dedicatedFields) {
    if (raw != null && String(raw).trim()) {
      period = detectCanonicalPeriod(String(raw));
      if (period) break;
    }
  }

  // Step 2: Parse từ market_type / marketType / market
  // (các field này encode period dạng prefix như "1st_half_point_spread")
  if (!period) {
    period = detectPeriodFromMarketStr(b?.market_type || b?.marketType || b?.market || "");
  }

  // Step 3: Parse từ marketScope / market_scope — hỗ trợ tất cả H1 variant
  // "spread::1st half", "spread::first_half", "h1", "1h", v.v.
  if (!period) {
    period = detectCanonicalPeriodFromScope(b?.marketScope || b?.market_scope || "");
  }

  // Final fallback
  if (!period) period = "full_time";

  const bookmaker = norm(b?.movementSourceBook || b?.bookmaker || b?.sharp_book || b?.sharpBook || "pinnacle");
  return [eventKey, mFamily, period, bookmaker].join("|");
}

function getSteamKickoffMs(bet) {
  const raw =
    bet?.kickoff ??
    bet?.kickoffDate ??
    bet?.startsAt ??
    bet?.fixture?.date ??
    bet?.fixture?.startTime ??
    null;
  if (!raw) return null;
  const ms = new Date(raw).getTime();
  return Number.isFinite(ms) ? ms : null;
}

function getSteamMinuteValue(bet) {
  const candidates = [
    bet?.matchMinute,
    bet?.minute,
    bet?.gameMinute,
    bet?.elapsed,
    bet?.elapsed_min,
    bet?.gameClock,
  ];
  for (const v of candidates) {
    if (v == null || v === "") continue;
    const n = typeof v === "number" ? v : parseInt(String(v), 10);
    if (Number.isFinite(n) && n >= 0 && n <= 130) return n;
  }
  return null;
}

function hasSteamLiveEvidenceForGameState(bet) {
  if (!bet || typeof bet !== "object") return false;

  const kickoffMs = getSteamKickoffMs(bet);
  const nowMs = Date.now();

  if (kickoffMs != null && kickoffMs > nowMs + 60_000) return false;

  const minute = getSteamMinuteValue(bet);
  if (minute != null) return true;

  const stateText = [
    bet?.gameState,
    bet?.status,
    bet?.matchStatus,
    bet?.state,
    bet?.period,
    bet?.phase,
    bet?.livePeriod,
  ].map((v) => String(v ?? "").toLowerCase()).join(" ");

  if (/\bin[_\s-]?play\b|\blive\b|\bplaying\b|\b1h\b|\b2h\b|\bfirst[_\s-]?half\b|\bsecond[_\s-]?half\b|\bhalftime\b|\bht\b/.test(stateText)) {
    return true;
  }

  if (bet?.isLive === true && (kickoffMs == null || kickoffMs <= nowMs + 60_000)) {
    return true;
  }

  return false;
}

const SteamCard = React.memo(function SteamCard({
  bet, lang, isFavorite, isFlashing, isEntering, isExiting, timeZone, t, steamMeta,
  onToggleFavorite, onOpenDetail, oddsFormat = "hk", favoriteSnapshot = null,
}) {
  const oddsLabel = t?.common?.oddsLabel || t?.common?.odds || "Odds";
  const trueOddsLabel = t?.common?.trueOddsLabel || t?.value?.trueOdds || "True Odds";

  const effectiveBet = React.useMemo(() => {
    if (!favoriteSnapshot) return bet;
    const snap = favoriteSnapshot;
    const snapCanShowGameState =
      hasSteamLiveEvidenceForGameState(snap) || hasSteamLiveEvidenceForGameState(bet);
    const liveMinute = snap.matchMinute ?? snap.minute ?? snap.gameMinute ?? null;
    const snapHasMinute = liveMinute != null && Number.isFinite(Number(liveMinute));
    const snapIsLive = snap.isLive === true && snapHasMinute ? true
      : snap.isLive === false ? false
      : bet.isLive ?? false;
    return {
      ...bet,
      minute:      snapCanShowGameState ? (liveMinute ?? bet.minute ?? null) : (bet.minute ?? null),
      matchMinute: snapCanShowGameState ? (liveMinute ?? bet.matchMinute ?? null) : (bet.matchMinute ?? null),
      gameClock:   snapCanShowGameState ? (snap.gameClock ?? bet.gameClock ?? null) : (bet.gameClock ?? null),
      homeScore:   snapCanShowGameState ? (snap.homeScore ?? bet.homeScore ?? null) : (bet.homeScore ?? null),
      awayScore:   snapCanShowGameState ? (snap.awayScore ?? bet.awayScore ?? null) : (bet.awayScore ?? null),
      isLive:      snapCanShowGameState ? snapIsLive : (bet.isLive ?? false),
      ...(snap.latestDrop ? {
        odds:     snap.latestDrop.newOdds ?? bet.odds,
        trueOdds: snap.latestDrop.oldOdds ?? bet.trueOdds,
        pct:      snap.latestDrop.dropPct ?? bet.pct,
      } : {}),
    };
  }, [bet, favoriteSnapshot, isFavorite]);

  const canShowGameState = hasSteamLiveEvidenceForGameState(effectiveBet);

  const statusInfo = getAnalysisStatusLabel(effectiveBet, t, lang, "—", timeZone);
  const displayLeague = translateLeagueDisplay(bet.league, lang);
  const selRaw = String(bet.selection ?? "").trim();
  const mRaw = String(bet.market ?? "").trim();
  const marketLine = getSteamMarketRowLabel(bet, lang);
  const displaySelection = translateSelectionDisplay(selRaw, lang);
  const showSel =
    selRaw &&
    mRaw.toLowerCase() !== selRaw.toLowerCase() &&
    !shouldHideSteamSelectionRow(marketLine, displaySelection);
  const tierMeta = getSteamTierMeta(bet, lang);
  const flagList = Array.isArray(bet.signalFlags)
    ? bet.signalFlags
    : bet.signalFlags
      ? [String(bet.signalFlags)]
      : [];

  const isPinnacleMove = bet?.isPinnacleMove === true;
  const displayFlags = flagList.filter((f) => !isDuplicateSteamFlag(f, { isPinnacleMove }));

  const handlePct =
    bet.splitsHandlePct != null && bet.splitsHandlePct !== ""
      ? Number(bet.splitsHandlePct)
      : null;
  const betPct =
    bet.splitsBetPct != null && bet.splitsBetPct !== ""
      ? Number(bet.splitsBetPct)
      : null;
  const handlePctOk = handlePct != null && Number.isFinite(handlePct);
  const betPctOk = betPct != null && Number.isFinite(betPct);
  const splitsLine =
    handlePctOk && betPctOk
      ? `${steamMeta.splits}: ${formatSteamPlainPct(handlePct)} / ${steamMeta.bets}: ${formatSteamPlainPct(betPct)}`
      : handlePctOk
        ? `${steamMeta.splits}: ${formatSteamPlainPct(handlePct)}`
        : betPctOk
          ? `${steamMeta.bets}: ${formatSteamPlainPct(betPct)}`
          : null;

  const sharpName = bet.sharp_book ?? bet.sharpBook;
  const devigName = bet.devig_book ?? bet.devigBook;
  const pinnacleExtra =
    tierMeta?.key === "green" &&
    String(sharpName || "").toLowerCase().includes("pinnacle") &&
    String(devigName || "").toLowerCase().includes("pinnacle")
      ? `${steamMeta.sharpDevig}: ${sharpName || "Pinnacle"}`
      : bet.hasPinnacleConfirm
        ? `${steamMeta.sharpDevig}: ${[sharpName, devigName].filter(Boolean).join(" / ") || "Pinnacle"}`
        : null;

  const oldOddsTok = isPinnacleMove ? formatOddsToken(bet.oldOdds, oddsFormat) : null;
  const newOddsTok = isPinnacleMove
    ? formatOddsToken(bet.newOdds != null ? bet.newOdds : bet.odds, oddsFormat)
    : null;
  const oldLineTok = isPinnacleMove ? formatLineToken(bet.oldLine) : null;
  const newLineTok = isPinnacleMove
    ? formatLineToken(bet.newLine != null ? bet.newLine : bet.line)
    : null;
  const lineChanged =
    isPinnacleMove &&
    oldLineTok != null &&
    newLineTok != null &&
    oldLineTok !== newLineTok;
  const oddsChanged =
    isPinnacleMove &&
    oldOddsTok != null &&
    newOddsTok != null &&
    oldOddsTok !== newOddsTok;
  const movePctTok = isPinnacleMove
    ? formatMovePctToken(bet.movePct, bet.moveDirection)
    : null;
  const ageTok = isPinnacleMove
    ? formatAgeToken(bet.movementAgeSeconds)
    : null;

  const moveItems = [];
  if (isPinnacleMove) {
    if (lineChanged) {
      moveItems.push({ label: "Line", value: `${oldLineTok} → ${newLineTok}`, type: "neutral" });
    } else if (newLineTok != null) {
      moveItems.push({ label: "Line", value: newLineTok, type: "neutral" });
    }
    if (oddsChanged) {
      moveItems.push({ label: "Odds", value: `${oldOddsTok} → ${newOddsTok}`, type: "neutral" });
    } else if (newOddsTok != null) {
      moveItems.push({ label: "Odds", value: newOddsTok, type: "neutral" });
    }
    if (movePctTok) {
      moveItems.push({
        label: "Move",
        value: movePctTok,
        type: bet.moveDirection === "down" ? "down" : bet.moveDirection === "up" ? "up" : "neutral",
      });
    }
    if (ageTok) {
      moveItems.push({ label: "Age", value: ageTok, type: "neutral" });
    }
    moveItems.push({ label: "Source", value: bet.movementSourceBook || "Pinnacle", type: "source" });
    if (Number.isFinite(Number(bet.confidenceScore))) {
      moveItems.push({ label: "Confidence", value: String(Math.round(Number(bet.confidenceScore))), type: "neutral" });
    }
  }

  const pressureDisplay = React.useMemo(
    () => getSteamPressureDisplay(effectiveBet),
    [effectiveBet]
  );

  const latestDropInfo = React.useMemo(
    () => getLatestDropInfo(effectiveBet),
    [effectiveBet]
  );

  // ── Compact card derived values ──────────────────────────────
  const compactLeague = abbreviateLeague(displayLeague);
  const teams = splitMatchTeams(bet.match);
  const homeDisplay = teams ? teams.home : null;
  const awayDisplay = teams ? teams.away : null;
  const rawHomeScore = effectiveBet.homeScore ?? effectiveBet.score?.home ?? bet.homeScore ?? bet.score?.home ?? null;
  const rawAwayScore = effectiveBet.awayScore ?? effectiveBet.score?.away ?? bet.awayScore ?? bet.score?.away ?? null;
  const homeScore = canShowGameState ? rawHomeScore : null;
  const awayScore = canShowGameState ? rawAwayScore : null;

  const cornersHome     = effectiveBet.cornersHome     ?? bet.cornersHome     ?? null;
  const cornersAway     = effectiveBet.cornersAway     ?? bet.cornersAway     ?? null;
  const yellowHome      = effectiveBet.yellowCardsHome ?? bet.yellowCardsHome ?? null;
  const yellowAway      = effectiveBet.yellowCardsAway ?? bet.yellowCardsAway ?? null;
  const redHome         = effectiveBet.redCardsHome    ?? bet.redCardsHome    ?? null;
  const redAway         = effectiveBet.redCardsAway    ?? bet.redCardsAway    ?? null;
  const hasCorners      = cornersHome != null && cornersAway != null;
  const hasYellow       = yellowHome  != null && yellowAway  != null;
  const hasRed          = redHome     != null && redAway     != null;
  const hasStats        = canShowGameState && (hasCorners || hasYellow || hasRed);

  const homePressure = pressureDisplay?.sidesArray?.[0] ?? null;
  const awayPressure = pressureDisplay?.sidesArray?.[1] ?? null;

  const bookShort = (() => {
    if (!bet.bookmaker) return "";
    const b = String(bet.bookmaker).toLowerCase();
    if (b.includes("pinnacle")) return "Pinnacle";
    return bet.bookmaker;
  })();
  function stripLineFromCompactMarket(label, lineToken) {
    if (!label || !lineToken) return label;
    const escaped = String(lineToken).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    return String(label)
      .replace(new RegExp(`\\s*·\\s*${escaped}\\s*$`, "i"), "")
      .trim();
  }

  function getCompactLineToken(b) {
    return (
      formatLineToken(
        b?.newLine ??
        b?.line ??
        b?.point ??
        b?.handicap ??
        b?.total
      ) || ""
    );
  }

  const compactLineToken = getCompactLineToken(bet);

  const compactMarketBase = stripLineFromCompactMarket(marketLine, compactLineToken);

  const compactMarket = bookShort
    ? `${compactMarketBase} · ${bookShort}`
    : compactMarketBase;

  const compactPick = steamLineAlreadyInSelection(displaySelection, compactLineToken)
    ? displaySelection
    : [displaySelection, compactLineToken].filter(Boolean).join(" ");

  // Card chính không hiển thị oldOdds → newOdds nữa
  // Chỉ hiển thị maxDropPct của group
  const oddsDisplay = "";
  // Chỉ hiện badge khi có _maxDropPct từ drops thật
  // Không fallback sang raw _groupPct hay bet.pct
  const impactStr = bet._maxDropPct != null
    ? formatSteamPct(bet._maxDropPct)
    : null;

  const isAggregateCard = !!bet._groupKey;

  const summaryLeadingSide =
    pressureDisplay?.leadingSide ||
    bet._leadingSide ||
    bet._maxDropSelection ||
    "";

  const summaryMaxDropPct =
    bet._maxDropPct != null ? Number(bet._maxDropPct) : null;

  const summaryLineCount = Number.isFinite(Number(bet._lineCount))
    ? Number(bet._lineCount)
    : 1;

  const summaryShiftFrom = formatLineToken(
    bet.openingLineValue ?? bet.lineShift?.openingLineValue
  );

  const summaryShiftTo = formatLineToken(
    bet.currentLineValue ??
    bet.closingLineValue ??
    bet.lineShift?.currentLineValue ??
    bet.lineShift?.closingLineValue
  );

  const summaryShiftAbsNum = Number(
    bet.lineShiftAbs ?? bet.lineShift?.lineShiftAbs
  );

  const summaryShiftAbs = Number.isFinite(summaryShiftAbsNum)
    ? String(Math.round(summaryShiftAbsNum * 100) / 100)
    : "";

  const showLineShift =
    bet.hasLineShiftSignal === true &&
    summaryShiftFrom &&
    summaryShiftTo &&
    Number.isFinite(summaryShiftAbsNum);

  // ── [PERF 3] SteamCard render counter ──
  if (isSteamPerfEnabled()) {
    __perfCardRenderCount++;
    if (!__perfCardLogTimer) {
      __perfCardLogTimer = setInterval(() => {
        if (isSteamPerfEnabled()) {
          console.log("[STEAM_PERF_CARD_RENDER]", { renders_per_5s: __perfCardRenderCount });
        }
        __perfCardRenderCount = 0;
      }, 5000);
    }
  }

  return (
    <div
      className={[
        "steam-compact-card",
        isFlashing ? "is-flash"    : "",
        isEntering ? "is-entering" : "",
        isExiting  ? "is-exiting"  : "",
        tierMeta   ? `is-tier-${tierMeta.key}` : "",
      ].filter(Boolean).join(" ")}
      onClick={() => onOpenDetail?.(bet)}
      style={{ cursor: "pointer" }}
    >
      <div className="steam-compact-top">
        <div className="steam-compact-top-left">
          <button
            type="button"
            className={`steam-fav-button${isFavorite ? " is-active" : ""}`}
            onClick={(e) => { e.stopPropagation(); onToggleFavorite(bet.id); }}
            aria-label={isFavorite ? t.common.removeFavorite : t.common.addFavorite}
            title={isFavorite ? t.common.removeFavorite : t.common.addFavorite}
          >
            {isFavorite ? "★" : "☆"}
          </button>
          <span className="steam-compact-league">{compactLeague}</span>
        </div>
        <span className="steam-compact-time">
          {(() => {
            if (!canShowGameState) return statusInfo.timeValue;
            const raw = effectiveBet.minute ?? effectiveBet.matchMinute ?? effectiveBet.gameClock ?? null;
            if (raw != null) {
              const n = typeof raw === "number" ? raw : parseInt(String(raw), 10);
              if (Number.isFinite(n) && n >= 0) return `${n}'`;
            }
            const p = String(effectiveBet.livePeriod ?? "").toUpperCase();
            if (p === "S1") return "H1";
            if (p === "S2") return "H2";
            if (p === "T1" || p === "T2" || p === "T3" || p === "T4") return "ET";
            if (p) return p;
            return statusInfo.timeValue;
          })()}
        </span>
      </div>

      {teams ? (
        <>
          <div className="steam-compact-team-row">
            <span className="steam-compact-team">
              {homeDisplay}{homeScore != null ? ` ${homeScore}` : ""}
              {homeScore != null && homeScore > 0 ? " ⚽" : ""}
            </span>
            {homePressure && (
              <span
                className="steam-compact-pressure"
                style={{
                  color: (pressureDisplay?.isLive ? homePressure.pressureShare : homePressure.prematchShare)
                      >= (pressureDisplay?.isLive ? (awayPressure?.pressureShare ?? 0) : (awayPressure?.prematchShare ?? 0))
                    ? "#66db8d" : "#f97316",
                }}
              >
                Steam {(pressureDisplay?.isLive
                  ? homePressure.pressureShare
                  : homePressure.prematchShare
                ).toFixed(0)}%
              </span>
            )}
          </div>
          <div className="steam-compact-team-row">
            <span className="steam-compact-team">
              {awayDisplay}{awayScore != null ? ` ${awayScore}` : ""}
              {awayScore != null && awayScore > 0 ? " ⚽" : ""}
            </span>
            {awayPressure && (
              <span
                className="steam-compact-pressure"
                style={{
                  color: (pressureDisplay?.isLive ? awayPressure.pressureShare : awayPressure.prematchShare)
                      >= (pressureDisplay?.isLive ? (homePressure?.pressureShare ?? 0) : (homePressure?.prematchShare ?? 0))
                    ? "#66db8d" : "#f97316",
                }}
              >
                Steam {(pressureDisplay?.isLive
                  ? awayPressure.pressureShare
                  : awayPressure.prematchShare
                ).toFixed(0)}%
              </span>
            )}
          </div>
        </>
      ) : (
        <div className="steam-compact-team-row">
          <span className="steam-compact-team">{bet.match}</span>
        </div>
      )}

      <div className="steam-compact-spacer" />

      <div className="steam-compact-market">
        <span>{compactMarketBase}</span>
        {bookShort ? <span className="steam-compact-book">{bookShort}</span> : null}
      </div>

      {isAggregateCard ? (
        <div className="steam-summary-block">
          {summaryLeadingSide && (
            <div className="steam-summary-row">
              <span className="steam-summary-label">Đang kéo về:</span>
              <span className="steam-summary-value">{summaryLeadingSide}</span>
            </div>
          )}
          {summaryMaxDropPct != null && summaryMaxDropPct > 0 && (
            <div className="steam-summary-row">
              <span className="steam-summary-label">Drop mạnh nhất:</span>
              <span className="steam-summary-value">{formatSteamPct(summaryMaxDropPct)}</span>
              <span className="steam-summary-spacer" />
              <span className="steam-summary-muted">Lines: {summaryLineCount}</span>
            </div>
          )}
          {showLineShift && (
            <div className="steam-summary-row">
              <span className="steam-summary-label">Line Shift:</span>
              <span className="steam-summary-value">{summaryShiftFrom} → {summaryShiftTo}</span>
              <span className="steam-summary-spacer" />
              <span className="steam-summary-delta">Δ{summaryShiftAbs}</span>
            </div>
          )}
          {hasStats && (
            <div className="steam-compact-pick-row">
              <span className="steam-compact-stats">
                {hasYellow && (
                  <span className="steam-stat-item">🟨 {yellowHome}–{yellowAway}</span>
                )}
                {hasRed && (redHome > 0 || redAway > 0) && (
                  <span className="steam-stat-item">🟥 {redHome}–{redAway}</span>
                )}
                {hasCorners && (
                  <span className="steam-stat-item">📐 {cornersHome}–{cornersAway}</span>
                )}
              </span>
            </div>
          )}
        </div>
      ) : (
        (compactPick || hasStats) ? (
          <div className="steam-compact-pick-row">
            {compactPick && <span className="steam-compact-pick">{compactPick}</span>}
            {hasStats && (
              <span className="steam-compact-stats">
                {hasYellow && (
                  <span className="steam-stat-item">🟨 {yellowHome}–{yellowAway}</span>
                )}
                {hasRed && (redHome > 0 || redAway > 0) && (
                  <span className="steam-stat-item">🟥 {redHome}–{redAway}</span>
                )}
                {hasCorners && (
                  <span className="steam-stat-item">📐 {cornersHome}–{cornersAway}</span>
                )}
              </span>
            )}
          </div>
        ) : null
      )}

      {(latestDropInfo?.timeStr || impactStr) && (
        <div className="steam-compact-footer">
          <span className="steam-compact-update">
            {latestDropInfo?.timeStr
              ? latestDropInfo.minute != null
                ? `↳ Cập nhật: ${latestDropInfo.minute}' • ${latestDropInfo.timeStr}`
                : `↳ Cập nhật: ${latestDropInfo.timeStr}`
              : ""}
          </span>
          {impactStr
            ? <span className="steam-compact-impact">{impactStr}</span>
            : null}
        </div>
      )}
    </div>
  );
});

export default function AnalysisPage({ active = true, lang = "en-GB", timeZone, oddsFormat = "hk" }) {
  const t = useMemo(() => getAnalysisTranslations(lang), [lang]);

  // ── [PERF MOUNT] log khi component mount ──
  useEffect(() => {
    if (isSteamPerfEnabled()) {
      console.log("[STEAM_PERF_MOUNT] AnalysisPage mounted", { active, lang });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [items, setItems] = useState([]);
  const [pctFilter, setPctFilter] = useState(1);
  const [mode, setMode] = useState("all"); // all | live | prematch
  const [isPctOpen, setIsPctOpen] = useState(false);
  const [isModeOpen, setIsModeOpen] = useState(false);
  const [lineMoveOnly, setLineMoveOnly] = useState(false);
  const [favoriteOnly, setFavoriteOnly] = useState(() => {
    try {
      return localStorage.getItem("analysis_favorite_only") === "true";
    } catch {
      return false;
    }
  });
  const [favoriteIds, setFavoriteIds] = useState(() => {
    try {
      const raw = localStorage.getItem(FAVORITES_STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      return new Set(Array.isArray(parsed) ? parsed : []);
    } catch {
      return new Set();
    }
  });

  const [flashIds, setFlashIds] = useState(new Set());
  const [detailBet, setDetailBet] = useState(null);
  const [modeMenuPos, setModeMenuPos] = useState(null);

  const [enteringIds, setEnteringIds] = useState(new Set());
  const [exitingIds,  setExitingIds]  = useState(new Set());
  const [visibleItems, setVisibleItems] = useState([]); // items đang hiển thị kể cả đang exit

  const exitTimerRef  = useRef(new Map()); // id → timeoutId

  const prevIds = useRef(new Set());
  const pollTimerRef = useRef(null);
  const inFlightRef = useRef(false);
  const abortRef = useRef(null);
  const flashClearTimeoutRef = useRef(null);
  const pctDropdownRef = useRef(null);
  const modeDropdownRef = useRef(null);
  const modeButtonRef = useRef(null);
  const modeRef = useRef(mode);
  const analysisWsRef = useRef(null);
  const analysisWsReconnectTimerRef = useRef(null);
  const analysisWsAttemptRef = useRef(0);
  const pushTransportActiveRef = useRef(false);
  const itemsTimestampRef = useRef(new Map()); // id → { receivedAt, lastOdds }
  const favoriteIdsRef = useRef(favoriteIds);
  useEffect(() => { favoriteIdsRef.current = favoriteIds; }, [favoriteIds]);
  const visibleItemsRef = useRef(visibleItems);
  useEffect(() => { visibleItemsRef.current = visibleItems; }, [visibleItems]);
  const itemsRef = useRef(items);
  useEffect(() => { itemsRef.current = items; }, [items]);
  const idToEventKeyRef = useRef(new Map());
  const [tick, setTick] = useState(0); // tăng mỗi 30s để re-evaluate expiry
  const [favoriteSnapshots, setFavoriteSnapshots] = useState({});
  const favoriteSnapshotTimerRef = useRef(null);
  const restAccessBlockedRef = useRef(false);

  const oddsLabel = t?.common?.oddsLabel || t?.common?.odds || "Odds";
  const trueOddsLabel = t?.common?.trueOddsLabel || t?.value?.trueOdds || "True Odds";
  const steamMeta = useMemo(() => getSteamMetaLabels(lang), [lang]);

  const blockSteamRestAccess = useCallback(() => {
    if (restAccessBlockedRef.current) return;

    restAccessBlockedRef.current = true;

    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }

    if (favoriteSnapshotTimerRef.current) {
      clearInterval(favoriteSnapshotTimerRef.current);
      favoriteSnapshotTimerRef.current = null;
    }

    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }

    if (analysisWsReconnectTimerRef.current) {
      clearTimeout(analysisWsReconnectTimerRef.current);
      analysisWsReconnectTimerRef.current = null;
    }

    setItems([]);
    setVisibleItems([]);
    setFavoriteSnapshots({});
    setDetailBet(null);
  }, []);

  useEffect(() => {
    modeRef.current = mode;
  }, [mode]);

  const updateModeMenuPos = useCallback(() => {
    if (!modeButtonRef.current) return;
    const rect = modeButtonRef.current.getBoundingClientRect();
    setModeMenuPos({
      top: rect.bottom + 6,
      left: rect.left,
      width: rect.width,
    });
  }, []);

  useEffect(() => {
    if (!isModeOpen) return;
    const sync = () => updateModeMenuPos();
    window.addEventListener("resize", sync);
    window.addEventListener("scroll", sync, true);
    return () => {
      window.removeEventListener("resize", sync);
      window.removeEventListener("scroll", sync, true);
    };
  }, [isModeOpen, updateModeMenuPos]);

  // Tick mỗi 30s để re-evaluate expiry (ẩn card hết hạn)
  useEffect(() => {
    const iv = setInterval(() => setTick((n) => n + 1), 30_000);
    return () => clearInterval(iv);
  }, []);

  function normalizeFixtureSnapshot(j) {
    const raw = j?.snapshot ?? j?.fixture ?? j?.data ?? j ?? {};
    const statusStr = String(raw.status ?? raw.matchStatus ?? raw.state ?? "").toLowerCase();
    const isLiveFromStatus =
      statusStr.includes("live") ||
      statusStr.includes("inplay") ||
      statusStr.includes("in-play") ||
      statusStr.includes("playing");
    const liveMinute =
      raw.matchMinute ?? raw.minute ?? raw.gameMinute ??
      raw.fixture?.minute ?? raw.state?.minute ?? null;
    const isLive =
      raw.isLive === true ||
      raw.live === true ||
      isLiveFromStatus;
    return {
      isLive,
      matchMinute: liveMinute,
      minute:      liveMinute,
      gameClock:   raw.gameClock ?? raw.clock ?? null,
      homeScore:   raw.homeScore ?? raw.home_score ?? raw.score?.home ?? null,
      awayScore:   raw.awayScore ?? raw.away_score ?? raw.score?.away ?? null,
      latestDrop:  raw.latestDrop ?? null,
    };
  }

  useEffect(() => {
    const poll = async () => {
      if (restAccessBlockedRef.current) return;

      const base = await detectAnalysisApiBase().catch(() => "");
      const now = Date.now();
      const SOON_MS = 30 * 60 * 1000; // 30 phút trước kickoff

      // Poll: card yêu thích + card sắp live (kickoff trong 30 phút) + card đang live
      const pollItems = (visibleItemsRef.current || []).filter((b) => {
        if (favoriteIdsRef.current.has(getCardGroupKeyStable(b))) return true;
        if (isLiveMatch(b)) return true;
        const ko = b.kickoffDate || b.kickoff || null;
        if (!ko) return false;
        const koMs = new Date(ko).getTime();
        return Number.isFinite(koMs) && koMs - now <= SOON_MS && koMs - now > -60 * 60 * 1000;
      });
      if (pollItems.length === 0) return;

      const results = await Promise.allSettled(
        pollItems.map(async (b) => {
          const eventId  = String(b.eventId  || b.event_id  || "").trim();
          const eventKey = String(b.event_key || b.stableKey || "").trim();
          if (!eventId && !eventKey) return null;
          const params = new URLSearchParams();
          if (eventId)  params.set("eventId",  eventId);
          if (eventKey) params.set("eventKey", eventKey);
          try {
            const res = await analysisAuthenticatedFetch(
              auth.currentUser,
              `${base}/api/analysis/fixture-snapshot?${params}`
            );
            if (!res.ok) return null;
            const j = await res.json();
            if (!j.ok) return null;
            return { id: getCardGroupKeyStable(b), snapshot: normalizeFixtureSnapshot(j) };
          } catch (error) {
            if (error?.name === "AbortError") return null;

            if (isBlockingSteamAccessError(error)) {
              blockSteamRestAccess();
              return null;
            }

            return null;
          }
        })
      );

      if (restAccessBlockedRef.current) return;

      const updates = {};
      results.forEach((r) => {
        if (r.status === "fulfilled" && r.value) {
          updates[r.value.id] = r.value.snapshot;
        }
      });
      if (Object.keys(updates).length > 0) {
        setFavoriteSnapshots((prev) => ({ ...prev, ...updates }));
      }
    };

    poll();
    favoriteSnapshotTimerRef.current = setInterval(poll, 15_000);
    return () => {
      if (favoriteSnapshotTimerRef.current) {
        clearInterval(favoriteSnapshotTimerRef.current);
        favoriteSnapshotTimerRef.current = null;
      }
    };
  }, [blockSteamRestAccess]); // refs keep poll() current — no restart on items/visibleItems/favoriteIds changes

  useEffect(() => {
    try {
      localStorage.setItem(FAVORITES_STORAGE_KEY, JSON.stringify(Array.from(favoriteIds)));
    } catch {
      // ignore storage errors
    }
  }, [favoriteIds]);

  useEffect(() => {
    try {
      localStorage.setItem("analysis_favorite_only", String(favoriteOnly));
    } catch {
      // ignore storage errors
    }
  }, [favoriteOnly]);

  useEffect(() => {
    if (!active) {
      pushTransportActiveRef.current = false;
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
      if (abortRef.current) {
        abortRef.current.abort();
        abortRef.current = null;
      }
      if (analysisWsReconnectTimerRef.current) {
        clearTimeout(analysisWsReconnectTimerRef.current);
        analysisWsReconnectTimerRef.current = null;
      }
      if (analysisWsRef.current) {
        try {
          analysisWsRef.current.close();
        } catch {
          /* ignore */
        }
        analysisWsRef.current = null;
      }
      return;
    }

    let cancelled = false;

    const applySteamJson = (j) => {
      if (restAccessBlockedRef.current) return;
      if (!j || j.ok !== true) return;
      // ── [PERF 1] applySteamJson frequency + duration ──
      const perfEnabled = isSteamPerfEnabled();
      const t0Apply = perfEnabled ? performance.now() : 0;
      if (perfEnabled) {
        const ts = Date.now();
        if (!__perfApplyWindowStart) __perfApplyWindowStart = ts;
        __perfApplyCount++;
        if (ts - __perfApplyWindowStart >= 10000) {
          console.log("[STEAM_PERF_APPLY_FREQ]", {
            calls_per_10s: __perfApplyCount,
            window_ms: ts - __perfApplyWindowStart,
          });
          __perfApplyCount = 0;
          __perfApplyWindowStart = ts;
        }
      }
      const rawList = Array.isArray(j.results) ? j.results : [];
      const now = Date.now();
      const data = rawList.map(normalizeSteamRow).filter(Boolean).map((x) => {
        if (x.id == null) return x;
        const prev = itemsTimestampRef.current.get(x.id);
        const oddsKey = String(x.odds ?? x.newOdd ?? "");
        // Follow-up only: chỉ update steamPressure, không reset thời gian
        if (x._followUpOnly === true && prev) {
          return { ...x, _receivedAt: prev.receivedAt };
        }
        if (prev && prev.lastOdds === oddsKey) {
          // Odds không đổi → giữ receivedAt cũ
          return { ...x, _receivedAt: prev.receivedAt };
        }
        // Card mới hoặc odds vừa cập nhật → reset thời gian
        itemsTimestampRef.current.set(x.id, { receivedAt: now, lastOdds: oddsKey });
        return { ...x, _receivedAt: now };
      });

      const newIds      = [];
      const removedIds  = [];
      const currentIds  = new Set(data.map((x) => x.id).filter(Boolean));
      // Cập nhật persistent map id → event_key (không xóa entry cũ)
      data.filter((x) => x.id && x.event_key).forEach((x) => {
        idToEventKeyRef.current.set(x.id, String(x.event_key).trim());
      });
      const idToEventKey = idToEventKeyRef.current;

      data.forEach((x) => {
        if (x.id != null && !prevIds.current.has(x.id) && x._followUpOnly !== true)
          newIds.push(x.id);
      });
      prevIds.current.forEach((id) => {
        if (currentIds.has(id)) return; // còn trong data mới → không remove
        // Tìm bet cũ để tính groupKey
        const oldBet = visibleItemsRef.current.find((b) => b.id === id);
        if (oldBet) {
          const favKey = getCardGroupKeyStable(oldBet);
          if (favoriteIdsRef.current.has(favKey)) return; // favorite → giữ lại
        } else {
          // Không tìm thấy bet cũ → fallback check id trực tiếp
          if (favoriteIdsRef.current.has(id)) return;
        }
        removedIds.push(id);
      });
      prevIds.current = currentIds;

      // Flash + Enter animation cho card mới
      if (newIds.length) {
        if (flashClearTimeoutRef.current) clearTimeout(flashClearTimeoutRef.current);
        setFlashIds(new Set(newIds));
        setEnteringIds(new Set(newIds));
        setTimeout(() => setEnteringIds(new Set()), 500);
        flashClearTimeoutRef.current = setTimeout(() => {
          setFlashIds(new Set());
          flashClearTimeoutRef.current = null;
        }, 2000);
      }

      // Exit animation: giữ card trong DOM 400ms trước khi xóa
      if (removedIds.length) {
        setExitingIds((prev) => {
          const next = new Set(prev);
          removedIds.forEach((id) => next.add(id));
          return next;
        });
        removedIds.forEach((id) => {
          if (exitTimerRef.current.has(id)) return;
          const timer = setTimeout(() => {
            setExitingIds((prev) => {
              const next = new Set(prev);
              next.delete(id);
              return next;
            });
            setVisibleItems((prev) => prev.filter((b) => b.id !== id));
            exitTimerRef.current.delete(id);
          }, 400); // khớp với duration steamExit trong CSS
          exitTimerRef.current.set(id, timer);
        });
      }

      // Dọn timestamp của card không còn trong list
      const activeIds = new Set(data.map((x) => x.id).filter(Boolean));
      for (const k of itemsTimestampRef.current.keys()) {
        if (!activeIds.has(k)) itemsTimestampRef.current.delete(k);
      }

      // Giữ lại card yêu thích dù server không gửi nữa
      setItems((prevItems) => {
        const serverIds = new Set(data.map((x) => x.id).filter(Boolean));
        const preserved = (prevItems || []).filter(
          (b) => b.id != null
            && !serverIds.has(b.id)
            && favoriteIdsRef.current.has(getCardGroupKeyStable(b))
        );
        const next = preserved.length ? [...data, ...preserved] : data;
        // Đồng bộ visibleItems: giữ card đang exit, thêm card mới
        setVisibleItems((prev) => {
          const exiting = prev.filter(
            (b) => exitTimerRef.current.has(b.id)
          );
          const nextIds = new Set(next.map((b) => b.id));
          const exitingOnly = exiting.filter((b) => !nextIds.has(b.id));
          return [...next, ...exitingOnly];
        });
        return next;
      });
      // ── [PERF 1] log sau setItems ──
      if (perfEnabled) {
        console.log("[STEAM_PERF_APPLY]", {
          rawList: rawList.length,
          data: data.length,
          ms: Math.round((performance.now() - t0Apply) * 10) / 10,
        });
      }
    };

    const restBootstrapOrPoll = async () => {
      if (restAccessBlockedRef.current) return;
      if (!active || inFlightRef.current) return;
      inFlightRef.current = true;
      if (abortRef.current) abortRef.current.abort();
      const ac = new AbortController();
      abortRef.current = ac;
      try {
        const base = await detectAnalysisApiBase();
        const url = `${base}/api/analysis/steam-move/list?scope=${encodeURIComponent("all")}`;
        const res = await analysisAuthenticatedFetch(
          auth.currentUser,
          url,
          { signal: ac.signal }
        );
        if (!res.ok) throw new Error(String(res.status));
        const j = await res.json();
        applySteamJson(j);
      } catch (error) {
        if (error?.name === "AbortError") return;

        if (isBlockingSteamAccessError(error)) {
          blockSteamRestAccess();
          return;
        }

        if (!pushTransportActiveRef.current) {
          setItems([]);
        }
      } finally {
        inFlightRef.current = false;
      }
    };

    const clearPoll = () => {
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    };

    const scheduleRestPoll = () => {
      clearPoll();
      const pollMs = modeRef.current === "prematch" ? 45000 : 8000;
      pollTimerRef.current = setInterval(() => {
        restBootstrapOrPoll();
      }, pollMs);
    };

    const clearWsReconnect = () => {
      if (analysisWsReconnectTimerRef.current) {
        clearTimeout(analysisWsReconnectTimerRef.current);
        analysisWsReconnectTimerRef.current = null;
      }
    };

    const scheduleWsReconnect = () => {
      clearWsReconnect();
      const attempt = analysisWsAttemptRef.current;
      const delayMs = Math.min(30000, Math.round(1000 * Math.pow(1.5, attempt)));
      analysisWsReconnectTimerRef.current = setTimeout(() => {
        analysisWsReconnectTimerRef.current = null;
        connectAnalysisWs();
      }, delayMs);
    };

    const connectAnalysisWs = async () => {
      if (cancelled || !active) return;
      const base = await detectAnalysisApiBase();
      const wsUrl = httpBaseToAnalysisWsUrl(base);
      if (!wsUrl || typeof WebSocket === "undefined") {
        pushTransportActiveRef.current = false;
        analysisWsAttemptRef.current = Math.min(analysisWsAttemptRef.current + 1, 20);
        await restBootstrapOrPoll();
        scheduleRestPoll();
        scheduleWsReconnect();
        return;
      }
      try {
        const ws = new WebSocket(wsUrl);
        analysisWsRef.current = ws;
        ws.onopen = () => {
          if (cancelled || analysisWsRef.current !== ws) return;
          analysisWsAttemptRef.current = 0;
          pushTransportActiveRef.current = true;
          clearPoll();
          clearWsReconnect();
        };
        ws.onmessage = (ev) => {
          if (cancelled || analysisWsRef.current !== ws) return;
          let msg;
          try {
            msg = JSON.parse(ev.data);
          } catch {
            return;
          }
          if (!msg || typeof msg !== "object") return;
          if (msg.type === "server_status" && msg.payload) {
            void msg.payload.analysisRevision;
          }
          if (msg.type === "steam_list" && msg.ok === true) {
            applySteamJson(msg);
          }
        };
        ws.onerror = () => {
          try {
            ws.close();
          } catch {
            /* ignore */
          }
        };
        ws.onclose = () => {
          if (analysisWsRef.current === ws) analysisWsRef.current = null;
          if (cancelled) return;
          pushTransportActiveRef.current = false;
          analysisWsAttemptRef.current = Math.min(analysisWsAttemptRef.current + 1, 20);
          scheduleRestPoll();
          scheduleWsReconnect();
        };
      } catch {
        pushTransportActiveRef.current = false;
        analysisWsAttemptRef.current = Math.min(analysisWsAttemptRef.current + 1, 20);
        await restBootstrapOrPoll();
        scheduleRestPoll();
        scheduleWsReconnect();
      }
    };

    (async () => {
      await restBootstrapOrPoll();
      if (cancelled) return;
      await connectAnalysisWs();
    })();

    return () => {
      cancelled = true;
      clearPoll();
      clearWsReconnect();
      pushTransportActiveRef.current = false;
      if (abortRef.current) {
        abortRef.current.abort();
        abortRef.current = null;
      }
      if (analysisWsRef.current) {
        try {
          analysisWsRef.current.close();
        } catch {
          /* ignore */
        }
        analysisWsRef.current = null;
      }
      if (flashClearTimeoutRef.current) {
        clearTimeout(flashClearTimeoutRef.current);
        flashClearTimeoutRef.current = null;
      }
    };
  }, [active, blockSteamRestAccess]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (pctDropdownRef.current && !pctDropdownRef.current.contains(e.target)) {
        setIsPctOpen(false);
      }

      const isModePortalClick = e.target?.closest?.(".analysis-mode-dropdown-layer, .analysis-mode-dropdown-portal");
      if (
        modeDropdownRef.current &&
        !modeDropdownRef.current.contains(e.target) &&
        !isModePortalClick
      ) {
        setIsModeOpen(false);
      }
    };

    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        setIsPctOpen(false);
        setIsModeOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside, { passive: true });
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  // Body scroll lock trong lúc Steam Detail overlay đang mở (portal vào document.body)
  useEffect(() => {
    if (!detailBet) return undefined;
    if (typeof document === "undefined") return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [detailBet]);

  const toggleFavorite = useCallback((id, groupKey) => {
    const bet = visibleItems.find((b) => b.id === id);
    const key = groupKey || (bet ? getCardGroupKeyStable(bet) : id);
    setFavoriteIds((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      // Sync ref ngay lập tức — tránh race condition với applySteamJson
      favoriteIdsRef.current = next;
      // Persist ngay vào localStorage
      try {
        localStorage.setItem(FAVORITES_STORAGE_KEY, JSON.stringify(Array.from(next)));
      } catch {}
      return next;
    });
  }, [visibleItems]);

  const LIVE_TTL_MS    = 5  * 60 * 1000; // 5 phút
  const PREMATCH_TTL_MS = 20 * 60 * 1000; // 20 phút

  const ALLOWED_MARKET_FAMILIES = new Set([
    "spread", "asian_handicap",
    "total", "over_under",
    "spread_corners", "asian_handicap_corners",
    "total_corners", "corners_total",
  ]);

  const filtered = useMemo(() => {
    const now = Date.now();
    // ── [PERF 2] filtered useMemo timing ──
    const perfEnabled = isSteamPerfEnabled();
    const t0Filtered = perfEnabled ? performance.now() : 0;
    let t0Agg = 0;

    // Cache getCardGroupKeyStable per-object trong phạm vi useMemo này
    const groupKeyCache = new Map();
    const getCachedGroupKey = (row) => {
      if (!row || typeof row !== "object") return getCardGroupKeyStable(row);
      if (!groupKeyCache.has(row)) {
        groupKeyCache.set(row, getCardGroupKeyStable(row));
      }
      return groupKeyCache.get(row);
    };

    // ── PASS 1: stableBase — filter không phụ thuộc user interaction ──
    // Những điều kiện này không thay đổi khi user đổi filter %, mode, favorite
    // → group composition ổn định → card không nhảy selection/line
    const stableBase = (items || []).filter((b) => {
      // Chỉ bóng đá
      const tab = classifySportForAnalysisTab(b.sport, { league: b.league, match: b.match });
      if (tab !== "football") return false;

      // Chỉ Pinnacle
      const srcBook = b.movementSourceBook || b.sharp_book || b.bookmaker || "";
      if (!/pinnacle/i.test(String(srcBook))) return false;

      // Chỉ market family cho phép
      const family = String(b.marketFamily || b.market_family || "").toLowerCase();
      if (!ALLOWED_MARKET_FAMILIES.has(family)) return false;

      // Chặn market thẻ phạt/cards
      const marketRaw = String(b.market || b.marketType || b.market_type || "").toLowerCase();
      const BLOCKED_MARKET_KEYWORDS = [
        "card", "booking", "yellow", "red_card", "cards",
        "thẻ", "phạt", "booking_handicap", "team_total",
      ];
      if (BLOCKED_MARKET_KEYWORDS.some((kw) => marketRaw.includes(kw))) return false;

      const isFav = favoriteIds.has(getCachedGroupKey(b));

      // 1. Hard expiry sau kickoff + 110 phút cho tất cả card (kể cả favorite)
      const ko = b.kickoffDate || b.kickoff || b.startTime || b.commence_time || null;
      if (ko) {
        const koMs = typeof ko === "number" ? ko : Date.parse(String(ko));
        if (Number.isFinite(koMs) && koMs > 0) {
          if (now > koMs + 110 * 60 * 1000) return false;
        }
      }

      // 2. Favorite được giữ lại trước mốc 110 phút (miễn TTL)
      if (isFav) return true;

      // 3. Non-favorite áp TTL bình thường
      if (b._receivedAt) {
        if (now - b._receivedAt > PREMATCH_TTL_MS) return false;
      }

      return true;
    });

    // ── Build groups từ stableBase ──
    const groupRowsMap = new Map();
    for (const b of stableBase) {
      const gk = getCachedGroupKey(b);
      if (!groupRowsMap.has(gk)) groupRowsMap.set(gk, []);
      groupRowsMap.get(gk).push(b);
    }

    // ── Build aggregate cards ──
    if (perfEnabled) t0Agg = performance.now();
    const allAggregates = [];
    for (const [gk, rows] of groupRowsMap) {
      if (rows.length === 0) continue;

      // Tính leading side từ tất cả drops của group
      const allDrops = rows.flatMap((r) =>
        Object.values(r.steamPressure?.sides || {})
          .flatMap((s) => Array.isArray(s.drops) ? s.drops : [])
      );
      const cumByLabel = {};
      for (const dr of allDrops) {
        const lbl = dr.side || dr.selection || "";
        if (!lbl) continue;
        if (!cumByLabel[lbl]) cumByLabel[lbl] = 0;
        const pctVal = typeof dr.dropPct === "number" ? dr.dropPct
          : typeof dr.singleDropPct === "number" ? dr.singleDropPct : 0;
        cumByLabel[lbl] += pctVal;
      }
      const sortedSides = Object.entries(cumByLabel).sort((a, b) => b[1] - a[1]);
      const leadingSideLabel = sortedSides[0]?.[0] ?? null;

      // Chọn representative: row của leadingSide + detectedAt mới nhất
      let rep = rows[0];
      if (leadingSideLabel) {
        const leadingRows = rows.filter((r) =>
          (r.selectionSide || r.selection || "")
            .toLowerCase()
            .includes(leadingSideLabel.toLowerCase().slice(0, 4))
        );
        const pool = leadingRows.length > 0 ? leadingRows : rows;
        rep = pool.reduce((best, r) => {
          const tR = r.detectedAt ? new Date(r.detectedAt).getTime() : (r._receivedAt || 0);
          const tB2 = best.detectedAt ? new Date(best.detectedAt).getTime() : (best._receivedAt || 0);
          return tR > tB2 ? r : best;
        }, pool[0]);
      }

      // maxDrop: drop lớn nhất trong group
      const maxDropRow = rows.reduce((best, r) =>
        (r.pct || 0) > (best.pct || 0) ? r : best, rows[0]);

      // groupPct = max pct trong group
      const groupPct = rows.reduce((mx, r) =>
        Math.max(mx, typeof r.pct === "number" ? r.pct : 0), 0);

      // lineBucket của group = lineBucket của maxDropRow
      const groupLineBucket = maxDropRow.lineBucket || rep.lineBucket || "mid";

      // Merge steamPressure từ tất cả rows trong group
      // Gom tất cả drops của mọi side từ mọi row
      const mergedPressure = (() => {
        if (rows.length === 1) return rows[0].steamPressure ?? null;
        const basePressure = rep.steamPressure;
        if (!basePressure?.sides) return basePressure ?? null;
        const mergedSides = Object.fromEntries(
          Object.entries(basePressure.sides || {}).map(([k, v]) => [
            k,
            {
              ...v,
              drops: Array.isArray(v?.drops) ? [...v.drops] : [],
            },
          ])
        );
        for (const r of rows) {
          if (r === rep || !r.steamPressure?.sides) continue;
          for (const [sideKey, sideData] of Object.entries(r.steamPressure.sides)) {
            if (!mergedSides[sideKey]) {
              mergedSides[sideKey] = { ...sideData };
            } else {
              const existingDrops = mergedSides[sideKey].drops || [];
              const newDrops = sideData.drops || [];
              const seen = new Set(existingDrops.map(d =>
                `${d.stableKey}|${d.oldOdds}|${d.newOdds}`
              ));
              for (const d of newDrops) {
                const dk = `${d.stableKey}|${d.oldOdds}|${d.newOdds}`;
                if (!seen.has(dk)) {
                  seen.add(dk);
                  existingDrops.push(d);
                }
              }
              mergedSides[sideKey].drops = existingDrops;
            }
          }
        }
        return { ...basePressure, sides: mergedSides };
      })();

      // Pick Line Shift từ bất kỳ row nào có hasLineShiftSignal (không chỉ rep)
      const _lsRow = rows.find((r) => r.hasLineShiftSignal === true);
      const _lsFields = _lsRow ? {
        hasLineShiftSignal:    true,
        openingLineValue:      _lsRow.openingLineValue,
        closingLineValue:      _lsRow.closingLineValue,
        lineShiftAbs:          _lsRow.lineShiftAbs,
        openingOdds:           _lsRow.openingOdds,
        closingOdds:           _lsRow.closingOdds,
        openingDetectedAt:     _lsRow.openingDetectedAt,
        closingDetectedAt:     _lsRow.closingDetectedAt,
        lineShiftLockedAt:     _lsRow.lineShiftLockedAt ?? null,
        lineShiftSelection:    _lsRow.lineShiftSelection,
        lineShiftMarket:       _lsRow.lineShiftMarket,
        lineShiftMarketFamily: _lsRow.lineShiftMarketFamily,
        lineShiftPeriod:       _lsRow.lineShiftPeriod,
        lineShiftBookmaker:    _lsRow.lineShiftBookmaker,
        lineShiftScope:        _lsRow.lineShiftScope,
      } : {};

      // Pick _mainLineMilestones: ưu tiên row khớp pair + side/selection của rep
      const _repPairLinePart = rep?.mainLinePairAbs != null
        ? `abs:${rep.mainLinePairAbs}`
        : rep?.mainLinePairLine != null
        ? `line:${rep.mainLinePairLine}`
        : null;

      const _repSel = String(
        rep?.selectionSide || rep?.selection || ""
      ).trim().toLowerCase();

      const _mlRow = rows.find((r) => {
        if (r._mainLineMilestones == null) return false;
        if (_repPairLinePart == null) return false;
        const k = String(r._mainLineMilestones.key || "");
        if (!k.includes(_repPairLinePart)) return false;
        const rSel = String(
          r.selectionSide || r.selection || ""
        ).trim().toLowerCase();
        return !_repSel || !rSel || rSel === _repSel;
      }) ?? rows.find((r) =>
        r._mainLineMilestones != null &&
        _repPairLinePart != null &&
        String(r._mainLineMilestones.key || "").includes(_repPairLinePart)
      ) ?? rows.find((r) => r._mainLineMilestones != null) ?? null;

      const _mlFields = _mlRow?._mainLineMilestones
        ? { _mainLineMilestones: _mlRow._mainLineMilestones }
        : {};

      // Pair filter: chỉ lấy drops của current pair — giống logic detail
      const _pairAbs = rep?.mainLinePairAbs != null
        ? Math.round(Math.abs(Number(rep.mainLinePairAbs)) * 1000) / 1000
        : null;

      const _pairLine = rep?.mainLinePairLine != null
        ? Math.round(Number(rep.mainLinePairLine) * 1000) / 1000
        : null;

      const _visibleDrops = Object.values(mergedPressure?.sides || {})
        .flatMap((s) => (Array.isArray(s.drops) ? s.drops : []))
        .filter((d) => {
          if (!d || d.movementType === "line_move") return false;
          if (d.excludeFromPressure === true) return false;
          const dLine = Number(d.line ?? d.newLine ?? d.oldLine);
          if (!Number.isFinite(dLine)) return true;
          const dRounded = Math.round(dLine * 1000) / 1000;
          const dAbs    = Math.round(Math.abs(dLine) * 1000) / 1000;
          if (_pairAbs  != null) return Math.abs(dAbs     - _pairAbs)  < 0.001;
          if (_pairLine != null) return Math.abs(dRounded  - _pairLine) < 0.001;
          return true;
        });

      // lineSet + lineCount từ _visibleDrops — đồng bộ với detail
      const lineSet = new Set(
        _visibleDrops
          .map((d) => {
            const n = Number(d.line ?? d.newLine ?? d.oldLine);
            if (!Number.isFinite(n)) return null;
            return _pairAbs != null
              ? String(Math.round(Math.abs(n) * 1000) / 1000)
              : String(Math.round(n * 1000) / 1000);
          })
          .filter(Boolean)
      );

      // Max single drop từ visible drops — đồng bộ với detail history
      const _displayMaxDrop = _visibleDrops
        .reduce((mx, d) => Math.max(mx, Number(d.singleDropPct ?? d.dropPct ?? 0)), 0);

      // _confirmedPct: chỉ lấy từ drops thật trong steamPressure
      // Không dùng raw groupPct (rows[].pct) để tránh card/detail lệch nguồn
      const _confirmedPct = _displayMaxDrop > 0 ? _displayMaxDrop : 0;

      // Pinnacle odds_delta movement-only signals carry pct at top-level,
      // not inside steamPressure.sides.drops — compute fallback pct from those fields
      const _isPinnacleOddsDeltaGroup = rows.some((r) =>
        r.isPinnacleMove === true ||
        String(r.movementSourceType || "").toLowerCase() === "odds_delta" ||
        /pinnacle/i.test(String(r.movementSourceBook || r.bookmaker || ""))
      );
      const _topLevelMovePct = (_isPinnacleOddsDeltaGroup && _confirmedPct === 0)
        ? Math.max(
            0,
            ...rows.flatMap((r) => [
              Number(r.pct || 0),
              Number(r.absPct || 0),
              Number(r.dropPct || 0),
              Number(r.movePct || 0),
              Number(r.oddsMovePct || 0),
              Number(r.movementStrengthPct || 0),
              Number(r.changePct || 0),
            ])
          )
        : 0;
      const _effectiveGroupPct = _confirmedPct > 0 ? _confirmedPct : _topLevelMovePct;

      allAggregates.push({
        ...rep,
        ..._lsFields,          // override lineShift fields nếu có
        ..._mlFields,          // milestone fields
        _groupKey:         gk,
        _rows:             rows,
        _lineCount:        lineSet.size,
        _groupPct:         _effectiveGroupPct,
        _maxDropPct:       _effectiveGroupPct > 0 ? _effectiveGroupPct : null,
        _maxDropLine:      maxDropRow.line ?? maxDropRow.newLine ?? null,
        _maxDropSelection: maxDropRow.selection ?? null,
        pct:               _effectiveGroupPct,
        lineBucket:        groupLineBucket,
        steamPressure:     mergedPressure,
      });
    }

    // ── PASS 2: filter aggregate cards theo user interaction ──
    // Chạy SAU khi đã build aggregate — không ảnh hưởng group composition
    const filtered2 = allAggregates.filter((agg) => {
      const live = isLiveMatch(agg);
      const isFav = favoriteIds.has(getCachedGroupKey(agg));

      // mode filter
      if (mode === "live" && !live) return false;
      if (mode === "prematch" && live) return false;

      // favorite only filter
      if (favoriteOnly && !isFav) return false;

      // lineMoveOnly filter
      if (lineMoveOnly && getBetLineMoveAbs(agg) < 0.5 && agg.hasLineShiftSignal !== true) return false;

      // pct filter — dùng _groupPct (includes odds_delta top-level fallback pct)
      if (agg.hasLineShiftSignal !== true && (agg._groupPct || 0) < pctFilter) return false;

      // Hard block: no confirmed drops — exempt Pinnacle odds_delta movement signals
      const _aggIsPinnacleOddsDelta =
        agg.isPinnacleMove === true ||
        String(agg.movementSourceType || "").toLowerCase() === "odds_delta" ||
        /pinnacle/i.test(String(agg.movementSourceBook || agg.bookmaker || ""));
      if (
        agg.hasLineShiftSignal !== true &&
        (agg._groupPct || 0) === 0 &&
        !_aggIsPinnacleOddsDelta
      ) return false;

      return true;
    });

    // ── [PERF 2] log filtered useMemo ──
    if (perfEnabled) {
      console.log("[STEAM_PERF_FILTERED]", {
        items_length:       (items || []).length,
        stableBase_length:  stableBase.length,
        groupRowsMap_size:  groupRowsMap.size,
        allAggregates_length: allAggregates.length,
        filtered2_length:   filtered2.length,
        agg_build_ms:       Math.round((performance.now() - t0Agg) * 10) / 10,
        total_ms:           Math.round((performance.now() - t0Filtered) * 10) / 10,
      });
    }

    const steamDebugEnabled =
      process.env.REACT_APP_STEAM_DEBUG === "true" ||
      (typeof window !== "undefined" && window.__STEAM_DEBUG === true);
    if (steamDebugEnabled) {
      const blockedByGroupPctGate = allAggregates.filter((agg) => {
        const _aggIsPinnacleOddsDelta =
          agg.isPinnacleMove === true ||
          String(agg.movementSourceType || "").toLowerCase() === "odds_delta" ||
          /pinnacle/i.test(String(agg.movementSourceBook || agg.bookmaker || ""));
        return (
          agg.hasLineShiftSignal !== true &&
          (agg._groupPct || 0) === 0 &&
          !_aggIsPinnacleOddsDelta
        );
      }).length;
      console.log("[SteamFilter]", {
        rawList: (items || []).length,
        afterStableBase: stableBase.length,
        afterAggregates: allAggregates.length,
        afterVisibleFilter: filtered2.length,
        blockedByGroupPctGate,
      });
    }

    return filtered2;
  }, [items, pctFilter, mode, favoriteOnly, favoriteIds, lineMoveOnly, tick]); // tick để re-render khi check expiry

  // Auto-update detail khi có drop mới — nằm sau filtered để tránh ReferenceError
  useEffect(() => {
    if (!detailBet) return;

    const countDrops = (bet) => {
      const sides = Object.values(bet?.steamPressure?.sides || {});
      return sides.reduce((s, side) => s + (Array.isArray(side?.drops) ? side.drops.length : 0), 0);
    };

    const latestDropTs = (bet) => {
      const sides = Object.values(bet?.steamPressure?.sides || {});
      let latest = 0;
      for (const side of sides) {
        const drops = Array.isArray(side?.drops) ? side.drops : [];
        for (const d of drops) {
          const rawTs = d?.detectedAt || d?.recordedAt || null;
          const ms = rawTs ? new Date(rawTs).getTime() : 0;
          if (Number.isFinite(ms) && ms > latest) latest = ms;
        }
      }
      return latest;
    };

    const totalDropPct = (bet) => {
      const sides = Object.values(bet?.steamPressure?.sides || {});
      let total = 0;
      for (const side of sides) {
        const drops = Array.isArray(side?.drops) ? side.drops : [];
        for (const d of drops) {
          if (d?.movementType === "line_move") continue;
          total += Number(d?.singleDropPct ?? d?.dropPct ?? 0) || 0;
        }
      }
      return Math.round(total * 10000) / 10000;
    };

    // Ưu tiên aggregate card cùng _groupKey
    let updated = null;
    if (detailBet._groupKey) {
      updated = filtered.find((b) => b?._groupKey === detailBet._groupKey) || null;
    }
    // Fallback cho non-aggregate item
    if (!updated) {
      const key = detailBet.stableKey || detailBet.id;
      if (key) updated = visibleItems.find((b) => (b.stableKey || b.id) === key) || null;
    }
    if (!updated) return;

    if (
      countDrops(updated)   !== countDrops(detailBet)   ||
      latestDropTs(updated) !== latestDropTs(detailBet) ||
      totalDropPct(updated) !== totalDropPct(detailBet)
    ) {
      setDetailBet(updated);
    }
  }, [filtered, visibleItems, detailBet]); // eslint-disable-line react-hooks/exhaustive-deps

  const modeTabs = [
    { key: "all", label: t?.common?.all || "Tất cả" },
    { key: "live", label: t?.common?.live || "Live" },
    { key: "prematch", label: t?.common?.prematch || "Sớm" },
  ];

  const activeModeLabel = modeTabs.find((x) => x.key === mode)?.label || "Tất cả";
  const steamListRowProps = useMemo(() => ({
    items: filtered,
    favoriteIds,
    flashIds,
    enteringIds,
    exitingIds,
    timeZone,
    t,
    steamMeta,
    toggleFavorite,
    setDetailBet,
    oddsFormat,
    favoriteSnapshots,
  }), [
    filtered,
    favoriteIds,
    flashIds,
    enteringIds,
    exitingIds,
    timeZone,
    t,
    steamMeta,
    toggleFavorite,
    setDetailBet,
    oddsFormat,
    favoriteSnapshots,
  ]);

  const renderSteamRow = useCallback(({
    index,
    style,
    items,
    favoriteIds: rowFavoriteIds,
    flashIds: rowFlashIds,
    enteringIds: rowEnteringIds,
    exitingIds: rowExitingIds,
    timeZone: rowTimeZone,
    t: rowT,
    steamMeta: rowSteamMeta,
    toggleFavorite: rowToggleFavorite,
    setDetailBet: rowSetDetailBet,
    oddsFormat: rowOddsFormat,
    favoriteSnapshots: rowFavoriteSnapshots,
    ariaAttributes,
  }) => {
    const agg = items[index];
    if (!agg) return null;
    const gk = agg._groupKey || agg.id;
    const repId = agg.id;
    const snapId = gk;
    return (
      <div
        {...ariaAttributes}
        style={{
          ...style,
          paddingBottom: STEAM_LIST_ROW_GAP,
          boxSizing: "border-box",
        }}
      >
        <SteamCard
          key={gk}
          bet={agg}
          lang={lang}
          isFavorite={rowFavoriteIds.has(gk) || rowFavoriteIds.has(getCardGroupKeyStable(agg))}
          isFlashing={rowFlashIds.has(repId)}
          isEntering={rowEnteringIds.has(repId)}
          isExiting={rowExitingIds.has(repId)}
          timeZone={rowTimeZone}
          t={rowT}
          steamMeta={rowSteamMeta}
          onToggleFavorite={(id) => rowToggleFavorite(id, gk)}
          onOpenDetail={rowSetDetailBet}
          oddsFormat={rowOddsFormat}
          favoriteSnapshot={
            (rowFavoriteIds.has(gk) || rowFavoriteIds.has(getCardGroupKeyStable(agg)) || isLiveMatch(agg))
              ? (rowFavoriteSnapshots[snapId] ?? null)
              : null
          }
        />
      </div>
    );
  }, [lang]);

  // ── [PERF 4] DOM card count vs filtered.length ──
  useEffect(() => {
    if (!isSteamPerfEnabled()) return;
    const timer = setTimeout(() => {
      const domCards = document.querySelectorAll(".steam-compact-card").length;
      console.log("[STEAM_PERF_DOM]", {
        filtered_length: filtered.length,
        dom_cards: domCards,
        virtualization_working: filtered.length > 0 && domCards < filtered.length,
      });
    }, 300);
    return () => clearTimeout(timer);
  }, [filtered]);

  return (
    <div className="analysis-page">
      <div className="analysis-filter-bar">
        <button
          type="button"
          onClick={() => setFavoriteOnly((prev) => !prev)}
          className={`analysis-filter-btn ${favoriteOnly ? "is-active" : ""}`}
          aria-pressed={favoriteOnly}
        >
          <span aria-hidden="true">{favoriteOnly ? "★" : "☆"}</span>
          <span>{t.common.favorites}</span>
        </button>

        <div ref={modeDropdownRef} className="analysis-select-wrap analysis-mode-select-wrap">
          <button
            ref={modeButtonRef}
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setIsPctOpen(false);
              updateModeMenuPos();
              setIsModeOpen((prev) => !prev);
            }}
            className={`analysis-select-button ${isModeOpen ? "is-open" : ""}`}
            aria-haspopup="listbox"
            aria-expanded={isModeOpen}
          >
            <span className="analysis-select-button-text">{activeModeLabel}</span>
            <span className="analysis-select-arrow">{isModeOpen ? "▴" : "▾"}</span>
          </button>

          {isModeOpen && modeMenuPos && createPortal(
            <div
              className="analysis-mode-dropdown-layer"
              onMouseDown={(e) => {
                if (e.target === e.currentTarget) {
                  setIsModeOpen(false);
                }
              }}
              onTouchStart={(e) => {
                if (e.target === e.currentTarget) {
                  setIsModeOpen(false);
                }
              }}
            >
              <div
                className="analysis-dropdown-menu analysis-mode-dropdown-menu analysis-mode-dropdown-portal"
                role="listbox"
                style={{
                  position: "fixed",
                  top: `${modeMenuPos.top}px`,
                  left: `${modeMenuPos.left}px`,
                  minWidth: `${modeMenuPos.width}px`,
                }}
                onMouseDown={(e) => e.stopPropagation()}
                onTouchStart={(e) => e.stopPropagation()}
              >
                {modeTabs.map((item) => {
                  const activeOption = !lineMoveOnly && mode === item.key;
                  return (
                    <button
                      key={item.key}
                      type="button"
                      role="option"
                      aria-selected={activeOption}
                      onClick={(e) => {
                        e.stopPropagation();
                        setLineMoveOnly(false);
                        setMode(item.key);
                        setIsModeOpen(false);
                      }}
                      className={`analysis-dropdown-item ${activeOption ? "is-active" : ""}`}
                    >
                      {item.label}
                    </button>
                  );
                })}
              </div>
            </div>,
            document.body
          )}
        </div>

        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setIsModeOpen(false);
            setIsPctOpen(false);
            setLineMoveOnly((prev) => {
              const next = !prev;
              if (next) {
                setMode("all");
              }
              return next;
            });
          }}
          className={`analysis-line-filter-btn ${lineMoveOnly ? "is-active" : ""}`}
          aria-pressed={lineMoveOnly}
          title="Chỉ hiện card có Line Move ≥0.5"
        >
          Line ≥0.5
        </button>

        <div ref={pctDropdownRef} className="analysis-select-wrap analysis-pct-select-wrap">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setIsModeOpen(false);
              setIsPctOpen((prev) => !prev);
            }}
            className={`analysis-select-button ${isPctOpen ? "is-open" : ""}`}
            aria-haspopup="listbox"
            aria-expanded={isPctOpen}
          >
            <span className="analysis-select-button-text">≥{pctFilter}%</span>
            <span className="analysis-select-arrow">{isPctOpen ? "▴" : "▾"}</span>
          </button>

          {isPctOpen && (
            <div className="analysis-dropdown-menu" role="listbox">
              {PCT_OPTIONS.map((value) => {
                const activeOption = pctFilter === value;
                return (
                  <button
                    key={value}
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setPctFilter(value);
                      setIsPctOpen(false);
                    }}
                    className={`analysis-dropdown-item ${activeOption ? "is-active" : ""}`}
                  >
                    ≥{value}%
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <div className="analysis-list">
        {filtered.length === 0 && exitingIds.size === 0 && (
          <div className="analysis-no-data">{t.steam.noSteam}</div>
        )}

        {filtered.length > 0 && (
          <List
            className="analysis-virtual-list"
            rowComponent={renderSteamRow}
            rowCount={filtered.length}
            rowHeight={STEAM_LIST_ROW_HEIGHT}
            rowProps={steamListRowProps}
            overscanCount={STEAM_LIST_OVERSCAN}
            style={{ height: "100%", width: "100%" }}
          />
        )}
      </div>

      {detailBet &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            className="analysis-detail-overlay"
            onClick={() => setDetailBet(null)}
            role="presentation"
          >
            <FixtureDetailPage
              bet={detailBet}
              onClose={() => setDetailBet(null)}
              oddsFormat={oddsFormat}
            />
          </div>,
          document.body
        )}

    </div>
  );
}
